// Expert Housing Converter
// Converts tolerance and parameter data to ExpertHousing objects

import type {ExpertHousing, ORingToleranceValue, ToleranceClass} from '~/types/oring-api-responses'
import type * as OringApiRequests from '@/types/oring-api'

interface ToleranceInputData {
  toleranceData: Record<string, string | null>
  parameterInputData: Record<string, number | null>
}

/**
 * Converts flat tolerance and parameter data to a properly structured ExpertHousing object
 */
export const useExpertHousingConverter = () => {
  /**
   * Convert toleranceData and parameterInputData to ExpertHousing object
   * @param baseHousing - The current housing object to use as a base
   * @param inputData - The tolerance and parameter input data from the UI
   * @param toleranceClasses - Available tolerance classes for lookup
   * @param toleranceParams - Current tolerance parameters for the geometry
   * @returns A new ExpertHousing object with updated values
   */
  function buildExpertHousingFromData(
    baseHousing: ExpertHousing | null,
    inputData: ToleranceInputData,
    toleranceClasses: ToleranceClass[],
    toleranceParams: OringApiRequests.ORingToleranceParameter[]
  ): ExpertHousing | null {
    if (!baseHousing) {
      // eslint-disable-next-line no-console
      console.log('base house was null')
      return null
    }

    // Create a deep copy of the current housing to avoid mutations
    const housing: ExpertHousing = JSON.parse(JSON.stringify(baseHousing))

    // Update tolerance values with user input data
    updateHousingWithToleranceData(housing, inputData, toleranceClasses, toleranceParams)

    return housing
  }

  /**
   * Update housing object with tolerance and parameter data from the UI
   */
  function updateHousingWithToleranceData(
    housing: ExpertHousing,
    inputData: ToleranceInputData,
    toleranceClasses: ToleranceClass[],
    toleranceParams: OringApiRequests.ORingToleranceParameter[]
  ): void {
    toleranceParams.forEach((param: OringApiRequests.ORingToleranceParameter) => {
      const key = param.toleranceParameterKey
      const toleranceClassId = inputData.toleranceData[key] ? parseInt(inputData.toleranceData[key]!) : null
      const parameterValue = inputData.parameterInputData[key]

      // Find the tolerance class object for additional data
      const toleranceClass = findToleranceClassById(toleranceClassId, toleranceClasses)

      // Update the specific field in the housing object
      updateHousingField(housing, key, parameterValue, toleranceClassId, toleranceClass)
    })
  }

  /**
   * Find a tolerance class by ID from the available tolerance classes
   */
  function findToleranceClassById(toleranceClassId: number | null, toleranceClasses: ToleranceClass[]): ToleranceClass | null {
    if (!toleranceClassId || !toleranceClasses) {
      return null
    }

    return toleranceClasses.find(tc => tc.toleranceClassId === toleranceClassId) || null
  }

  /**
   * Update a specific field in the housing object with new tolerance/parameter data
   */
  function updateHousingField(
    housing: ExpertHousing,
    key: string,
    parameterValue: number | null,
    toleranceClassId: number | null,
    toleranceClass: ToleranceClass | null
  ): void {
    // Helper function to create or update an ORingToleranceValue
    const createOrUpdateToleranceValue = (existing: ORingToleranceValue | null): ORingToleranceValue => {
      const base = existing || {} as ORingToleranceValue

      return {
        ...base,
        toleranceClassId: toleranceClassId || base.toleranceClassId || 0,
        toleranceClassName: toleranceClass?.toleranceClassName || base.toleranceClassName || null,
        parameterValue: parameterValue ?? (base.parameterValue || 0),
        val: parameterValue ?? (base.val || 0),
        roundedVal: parameterValue !== null ? Math.round(parameterValue * 1000) / 1000 : base.roundedVal || 0,
        // Keep existing tolerance calculations or set defaults
        toleranceMin: base.toleranceMin || 0,
        toleranceMax: base.toleranceMax || 0,
        parameterMin: base.parameterMin || 0,
        parameterMax: base.parameterMax || 0,
        isEqual: base.isEqual || false
      }
    }

    // Update the appropriate field based on the key
    switch (key) {
    case 'd1':
      if (!housing.oRing) {
        housing.oRing = {
          d1: {} as ORingToleranceValue,
          d2: {} as ORingToleranceValue,
          materialName: null
        }
      }
      housing.oRing.d1 = createOrUpdateToleranceValue(housing.oRing.d1)
      break
    case 'd2':
      if (!housing.oRing) {
        housing.oRing = {
          d1: {} as ORingToleranceValue,
          d2: {} as ORingToleranceValue,
          materialName: null
        }
      }
      housing.oRing.d2 = createOrUpdateToleranceValue(housing.oRing.d2)
      break
    case 'r1':
      housing.r1 = createOrUpdateToleranceValue(housing.r1)
      break
    case 'd3':
      housing.d3 = createOrUpdateToleranceValue(housing.d3)
      break
    case 'd4':
      housing.d4 = createOrUpdateToleranceValue(housing.d4)
      break
    case 'd5':
      housing.d5 = createOrUpdateToleranceValue(housing.d5)
      break
    case 'd6':
      housing.d6 = createOrUpdateToleranceValue(housing.d6)
      break
    case 'd8':
      housing.d8 = createOrUpdateToleranceValue(housing.d8)
      break
    case 'd9':
      housing.d9 = createOrUpdateToleranceValue(housing.d9)
      break
    case 'd10':
      housing.d10 = createOrUpdateToleranceValue(housing.d10)
      break
    case 'b1in':
    case 'b1out':
      housing.b1 = createOrUpdateToleranceValue(housing.b1)
      break
    case 'b4':
      housing.b4 = createOrUpdateToleranceValue(housing.b4)
      break
    case 'gap':
      housing.gapWidth = createOrUpdateToleranceValue(housing.gapWidth)
      break
    case 'height':
      housing.h = createOrUpdateToleranceValue(housing.h)
      break
    default:
      // Unknown tolerance parameter key - ignore silently
      break
    }
  }

  return {
    buildExpertHousingFromData,
    updateHousingWithToleranceData,
    findToleranceClassById,
    updateHousingField
  }
}
